import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { cloneDeep, isEmpty } from "lodash";
import {
  swatch,
  downloadIcon,
  colouredAddCircle,
} from "../../assets/components/svgs";
import InputField from "../shared/Fields/InputField";
import {
  getLanguageFileName,
  isValidLocale,
  getLanguageName,
  getGlobalBucketName,
  updateLocalization,
  isValidUrl,
  updatePrivacyUrl,
} from "./Util";
import Clipboard from "../../assets/clipboard.svg";
import Dropzone from "../shared/DragAndDrop/Dropzone";
import { IconButton } from "../primitives/buttons/IconButton";
import BiButton from "../primitives/buttons/BiButton.primitive";
import LoaderContainer from "../shared/loaderContainer/LoaderContainer";
import PickList, { PICKLIST_TYPES } from "../shared/Fields/PickList";
import { AppConstants } from "../../constants/AppConstants";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setToastData } from "../../app/toastReducer";
import MenuButtonsPortal from "../Menu/MenuButtonsPortal";
import Card from "../shared/cards/Card";
import { getGCPFile, listFiles } from "../../http/asset-management-services";
import { HTTP_STATUS } from "../../http/constants/http.status";
import UploadLanguageCard from "./UploadLanguageCard";
import { useLocalization } from "../../hooks/useLocalization";

export interface LanguageObject {
  file: any;
  languageName: string;
  languageNameError: string;
  localeNameError: string;
  localeName: string;
  createMode: boolean;
  listOptions: Array<any>;
  currentOption: Array<any>;
}

enum LanguageObjectKeys {
  file,
  languageName,
  languageNameError,
  localeNameError,
  localeName,
  createMode,
  listOptions,
  currentOption,
}

export const defaultLanguageSchema = {
  file: null,
  languageName: "",
  languageNameError: "",
  localeName: "",
  localeNameError: "",
  createMode: false,
  listOptions: [],
  currentOption: [],
};

/**
 * @parentId customization
 * @manager Policies
 * @overview Manage the Policies section in the application, including rules, disclaimers, adverse events, and privacy policy.
 * <section>
 *   <p>
 *     The Policies section allows admin users to manage necessary policy documents, including rules, disclaimers, adverse events, and the privacy policy.
 *     Admin users can upload, update, and view these policy documents as needed.
 *   </p>
 *   <p>
 *   These policy documents are primarily stored on the Google Cloud Platform Bucket.
 *   </p>
 *   <p>
 *     <b>Rules Language Packs</b><br/>
 *     Rules are a set of instructions displayed when a user clicks on <strong>About Data Subject Rights</strong> on the Login Page, in accordance with the current locale of the application.
 *     Admin users can create a new Rules Language pack by selecting the language and locale, uploading the JSON file, and saving the changes.
 *     <br>
 *     After selecting a language pack in the Rules Language Pack section, a download button is located at the bottom. Clicking on it will download the JSON File.
 *     Admin users can also update existing Rules Language Pack by selecting the Language, uploading the JSON file, and saving the changes.
 *     <br/><br/>
 *     <b>Privacy Policy</b><br/>
 *     Admin users can manage the privacy policy by entering the privacy policy URL and saving the changes. This is in the form of a text-field that takes the URL of the privacy policy page.
 *     <br>
 *     This URL is opened when a user clicks on <strong>Application Privacy Notice</strong> on the Login Page.
 *     It is maintained in a JSON file config.json.
 *     <br>
 *     Once the User updates the privacy URL and clicks on the Save button, it calls the AMS-05 Create Signed URL which, on success, calls the Upload GCP File PUT Call and updates the config.json file in the bucket.
 *     <br/><br/>
 *     <b>Disclaimer Policy</b><br/>
 *     A set of information that is localized in different languages used to indicate crucial information regarding the request submission.
 *     This information is shown below the form in the Create Request Page, displayed in accordance with the current locale of the application.
 *     Admin users can create a new Disclaimer Language pack by selecting the language and locale, uploading the JSON file, and saving the changes.
 *     <br>
 *     After selecting a language pack in the Disclaimer Policy Language Pack section, a download button is located at the bottom. Clicking on it will download the JSON File.
 *     Admin users can also update existing Disclaimer Policy Language Pack by selecting the Language, uploading the JSON file, and saving the changes.
 *     <br/><br/>
 *     <b>Data Requests Adherence Policy</b><br/>
 *     A set of information localized in different languages (mainly English, Spanish, Japanese, and French) and is only country-specific (mainly United States:us, Argentina:ar, Mexico:mx, Chile:cl, Canada:ca).
 *     They provide various contact details for health centers in different countries where users can report any negative side effects.
 *     This information is shown below the form in the Create Request Page, displayed in accordance with the current locale and country of the application.
 *     Admin users can create a new Data Requests Adherence Policy Language pack by selecting the language and locale, uploading the JSON file, and saving the changes.
 *     <br>
 *     After selecting a language pack in the Data Requests Adherence Language Pack section, a download button is located at the bottom. Clicking on it will download the JSON File.
 *     Admin users can also update existing Data Requests Adherence Language Pack by selecting the Language, uploading the JSON file, and saving the changes.
 *     <br/><br/>
 *   </p>
 *   <p>
 *     <h4>Policies Form Fields</h4>
 *     The Policies section will display the following fields with the specified validation constraints.
 *     <br/>
 *     <table>
 *       <tr>
 *         <th>Field Name</th>
 *         <th>Required</th>
 *         <th>Data Type</th>
 *         <th>Field Type</th>
 *         <th>Constraints</th>
 *         <th>Editable</th>
 *       </tr>
 *       <tr>
 *         <td>Language Name</td>
 *         <td>Yes</td>
 *         <td>String</td>
 *         <td>Textfield</td>
 *         <th>NA</th>
 *         <td>Yes</td>
 *       </tr>
 *       <tr>
 *         <td>Locale Name</td>
 *         <td>Yes</td>
 *         <td>String</td>
 *         <td>Textfield</td>
 *         <th>NA</th>
 *         <td>Yes</td>
 *       </tr>
 *       <tr>
 *         <td>File</td>
 *         <td>Yes</td>
 *         <td>JSON File</td>
 *         <td>Drag Drop or Button</td>
 *         <th>.json format</th>
 *         <td>Yes</td>
 *       </tr>
 *       <tr>
 *         <td>Privacy Policy URL</td>
 *         <td>No</td>
 *         <td>String</td>
 *         <td>Textfield</td>
 *         <th>Valid URL format</th>
 *         <td>Yes</td>
 *       </tr>
 *     </table>
 *     <br/>
 *   </p>
 * </section>
 * <section>
 *   <h3>Functionality</h3>
 *   <section>
 *     <h3>Create/Update Rules, Disclaimer, and Adverse Events</h3>
 *     Admin users can create or update rules, disclaimer, and adverse events by selecting the language, locale, and uploading the corresponding JSON file. Saving the changes initiates the process.
 *   </section>
 *   <section>
 *     <h3>Manage Privacy Policy</h3>
 *     Admin users can manage the privacy policy by entering the privacy policy URL and saving the changes.
 *   </section>
 * </section>
 * <section>
 *   <h3>Failure Status Codes</h3>
 *   <p> This section describes the Policies Status Code information. <br>
 *     <table>
 *       <tr>
 *         <th>HTTP Status Code</th>
 *         <th>Service Error Code</th>
 *         <th>Error Message</th>
 *       </tr>
 *       <tr>
 *         <td>500</td>
 *         <td>AMS_INTERNAL_ERROR</td>
 *         <td>Internal Server Error</td>
 *       </tr>
 *       <tr>
 *         <td>503</td>
 *         <td></td>
 *         <td>Service Unavailable</td>
 *       </tr>
 *     </table>
 *   </p>
 * </section>
 * <section>
 *   <h3>Success Status Codes</h3>
 *   <p> This section describes the Policies Status Code information. <br>
 *     <table>
 *       <tr>
 *         <th>HTTP Status Code</th>
 *         <th>Service Code</th>
 *         <th>Message</th>
 *       </tr>
 *       <tr>
 *         <td>200</td>
 *         <td>AMS_SUCCESS</td>
 *         <td>Operation completed successfully.</td>
 *       </tr>
 *       <tr>
 *         <td>201</td>
 *         <td>AMS_FILE_UPLOADED</td>
 *         <td>File uploaded successfully.</td>
 *       </tr>
 *     </table>
 *   </p>
 * </section>
 */

const Policies = () => {
  const { t } = useTranslation();
  const { getRTLLayout } = useLocalization();
  const rtlClasses = getRTLLayout();
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);

  const [privacyUrl, setPrivacyUrl] = useState({
    value: "",
    error: "",
    orignalValue: "",
  });

  const rulesFileRef = useRef<any>(null);
  const disclaimerFileRef = useRef<any>(null);
  const adverseEventsFileRef = useRef<any>(null);

  const [rulesData, setRulesData] = useState<LanguageObject>(
    defaultLanguageSchema
  );
  const [disclaimerData, setDisclaimerData] = useState<LanguageObject>(
    defaultLanguageSchema
  );
  const [adverseEventsData, setAdverseEventsData] = useState<LanguageObject>(
    defaultLanguageSchema
  );

  const dataSetter = (
    type: "rules" | "disclaimer" | "adverse",
    key:
      | "file"
      | "languageName"
      | "languageNameError"
      | "localeNameError"
      | "localeName"
      | "createMode"
      | "listOptions"
      | "currentOption",
    value: any
  ) => {
    switch (type) {
      case "rules":
        setRulesData({ ...rulesData, [key]: value });
        break;
      case "adverse":
        setAdverseEventsData({ ...adverseEventsData, [key]: value });
        break;
      case "disclaimer":
        setDisclaimerData({ ...disclaimerData, [key]: value });
        break;

      default:
        break;
    }
  };

  const userData = useAppSelector(
    (state: any) => state.userManagement.userProfileInfo
  );

  const getList = async (
    type: "rules" | "disclaimer" | "adverse" = "rules",
    discard: boolean = false
  ) => {
    try {
      let payload = {
        folderPath:
          type === "rules"
            ? `${AppConstants.getRulesGCPPath()}`
            : type === "adverse"
            ? `${AppConstants.getAdverseGCPPath()}`
            : `${AppConstants.getDisclaimerGCPPath()}`,
        expirationTime: 30,
        generateSignedUrl: true,
        bucketName: getGlobalBucketName(),
      };
      let response = await listFiles(payload, {
        page: AppConstants.DEFAULT_PAGE,
        size: AppConstants.MAXIMUM_PAGE_SIZE,
      });
      const { data, status } = response;
      if (HTTP_STATUS.isSuccess(status)) {
        if (data.data.length > 0) {
          {
            let displayNameArr = data.data
              .filter((files: any) => files.type === AppConstants.GCP_FILES)
              .map((obj: any) => ({
                ...obj,
                displayName:
                  obj.type === AppConstants.GCP_FILES
                    ? getLanguageName(obj)
                    : "",
              }));

            if (discard) {
              switch (type) {
                case "rules":
                  setRulesData({
                    ...defaultLanguageSchema,
                    listOptions: displayNameArr,
                  });
                  break;
                case "adverse":
                  setAdverseEventsData({
                    ...defaultLanguageSchema,
                    listOptions: displayNameArr,
                  });
                  break;
                case "disclaimer":
                  setDisclaimerData({
                    ...defaultLanguageSchema,
                    listOptions: displayNameArr,
                  });
                  break;

                default:
                  break;
              }
            } else {
              dataSetter(type, "listOptions", displayNameArr);
            }
          }
        }
        setIsLoading(false);
      }
    } catch (e) {
      setIsLoading(false);
    }
  };

  const getPrivacyUrl = async () => {
    try {
      let response = await getGCPFile(
        AppConstants.getConfigFullPath(),
        {
          [AppConstants.HEADER_ACCEPT]: AppConstants.VALUE_APPLICATION_JSON,
        }
      );
      const { data, status } = response;
      if (HTTP_STATUS.isSuccess(status)) {
        if (data?.privacyUrl) {
          setPrivacyUrl({
            value: data.privacyUrl,
            error: "",
            orignalValue: data.privacyUrl,
          });
        }

        setIsLoading(false);
        return;
      }
      // setPrivacyUrl({
      //   value: AppConstants.DEFAULT_PRIVACY_URL,
      //   error: "",
      //   orignalValue: AppConstants.DEFAULT_PRIVACY_URL,
      // });
    } catch (e) {
      // setPrivacyUrl({
      //   value: AppConstants.DEFAULT_PRIVACY_URL,
      //   error: "",
      //   orignalValue: AppConstants.DEFAULT_PRIVACY_URL,
      // });
      setIsLoading(false);
    }
  };

  async function initialize() {
    try {
      await getPrivacyUrl();
      await getList("rules");
      await getList("disclaimer");
      await getList("adverse");
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    initialize();
  }, []);

  const discardChanges = () => {
    if (rulesFileRef?.current?.value) {
      rulesFileRef.current.value = "";
    }
    if (disclaimerFileRef?.current?.value) {
      disclaimerFileRef.current.value = "";
    }
    if (adverseEventsFileRef?.current?.value) {
      adverseEventsFileRef.current.value = "";
    }

    setRulesData(defaultLanguageSchema);
    setDisclaimerData(defaultLanguageSchema);
    setAdverseEventsData(defaultLanguageSchema);
  };

  const saveChanges = async () => {
    const rulesFlag = isEmpty(rulesData.file);
    const disclaimerFlag = isEmpty(disclaimerData.file);
    const adverseFlag = isEmpty(adverseEventsData.file);
    const privacyFlag = privacyUrl.value === privacyUrl.orignalValue;

    if (!rulesFlag) {
      const { createMode, localeName } = rulesData;
      if (createMode && !isValidLocale(localeName)) {
        dataSetter(
          "rules",
          "localeNameError",
          t(`T_VALID_LOCALE_FORMAT`).replace("%s", localeName)
        );
        return;
      }
      await savePayload(rulesData, async () => {
        if (rulesFileRef?.current?.value) {
          rulesFileRef.current.value = "";
        }
        if (disclaimerFileRef?.current?.value) {
          disclaimerFileRef.current.value = "";
        }
        if (adverseEventsFileRef?.current?.value) {
          adverseEventsFileRef.current.value = "";
        }
        dispatch(
          setToastData({
            toastMessage: t("T_LANGUAGE_RULE_UPDATED_SUCCESS"),
            isToastActive: true,
            toastType: "success",
          })
        );

        await getList("rules", true);
      });
    }

    if (!privacyFlag) {
      if (privacyUrl.value.length && !isValidUrl(privacyUrl.value)) {
        setPrivacyUrl({ ...privacyUrl, error: t("T_ENTER_VALID_PRIVACY_URL") });
        return;
      }

      try {
        await updatePrivacyUrl(privacyUrl.value, userData.userId);
        dispatch(
          setToastData({
            toastMessage: t("T_PRIVACY_URL_UPDATED_SUCCESS"),
            isToastActive: true,
            toastType: "success",
          })
        );
        setPrivacyUrl({ ...privacyUrl, orignalValue: privacyUrl.value });
      } catch (err: any) {
        setToastData({
          toastMessage: t("T_ERROR_SOMETHING_WRONG"),
          isToastActive: true,
          toastType: "error",
        });
      }
    }
    if (!disclaimerFlag) {
      const { createMode, localeName } = disclaimerData;
      if (createMode && !isValidLocale(localeName)) {
        dataSetter(
          "disclaimer",
          "localeNameError",
          t(`T_VALID_LOCALE_FORMAT`).replace("%s", localeName)
        );
        return;
      }
      await savePayload(disclaimerData, async () => {
        setDisclaimerData(defaultLanguageSchema);
        if (disclaimerFileRef?.current?.value) {
          disclaimerFileRef.current.value = "";
        }
        dispatch(
          setToastData({
            toastMessage: t("T_LANGUAGE_DISCLAIMER_UPDATED_SUCCESS"),
            isToastActive: true,
            toastType: "success",
          })
        );

        await getList("disclaimer", true);
      });
    }

    if (!adverseFlag) {
      const { createMode, localeName } = adverseEventsData;
      if (createMode && !isValidLocale(localeName)) {
        dataSetter(
          "adverse",
          "localeNameError",
          t(`T_VALID_LOCALE_FORMAT`).replace("%s", localeName)
        );
        return;
      }
      await savePayload(adverseEventsData, async () => {
        setAdverseEventsData(defaultLanguageSchema);
        if (adverseEventsFileRef?.current?.value) {
          adverseEventsFileRef.current.value = "";
        }
        dispatch(
          setToastData({
            toastMessage: t("T_LANGUAGE_ADVERSE_UPDATED_SUCCESS"),
            isToastActive: true,
            toastType: "success",
          })
        );

        await getList("adverse", true);
      });
    }
  };

  const savePayload = async (langFile: LanguageObject, callback: Function) => {
    let flag = !isEmpty(langFile.file);

    function errorHandling(errorText: string) {
      setIsLoading(false);
      dispatch(
        setToastData({
          toastMessage: errorText,
          isToastActive: true,
          toastType: "error",
        })
      );
    }

    if (flag) {
      setIsLoading(true);
      updateLocalization(langFile.file, userData?.userId, langFile.languageName)
        .then(async (response) => {
          callback();
        })
        .catch((error) => {
          errorHandling(t("T_ERROR_SOMETHING_WRONG"));
        });
    }
  };

  return (
    <LoaderContainer isLoading={isLoading}>
      <MenuButtonsPortal>
        <BiButton
          className={`tertiary ${rtlClasses.marginRight} border py-1.5`}
          type="button"
          onClick={discardChanges}
        >
          {t("T_CANCEL")}
        </BiButton>

        <BiButton
          className={"bg-primary py-1.5 text-white"}
          type="button"
          disabled={
            isEmpty(rulesData.file) &&
            isEmpty(disclaimerData.file) &&
            isEmpty(adverseEventsData.file) &&
            privacyUrl.value === privacyUrl.orignalValue
          }
          onClick={saveChanges}
        >
          {t("T_SAVE")}
        </BiButton>
      </MenuButtonsPortal>
      <div className="p-4">
        <UploadLanguageCard
          title={t("T_LANGUAGE_RULE_PACKS")}
          setData={setRulesData}
          ref={rulesFileRef}
          data={rulesData}
          addButtonTitle={t("T_LANGUAGE_RULE_ADD_BUTTON")}
          pickerTitle={t("T_LANGUAGE")}
          fileKey={`${AppConstants.getRulesGCPPath()}${AppConstants.ASSETS_LOCALISATION_RULES_FILE_PREFIX}${rulesData.localeName}.${AppConstants.DATA_TYPE_JSON}`}
        ></UploadLanguageCard>

        <Card
          title={t("T_PRIVACY_POLICY")}
          icon={Clipboard}
          alt="Clip Icon"
          height=""
          width=""
          className="w-3/4"
        >
          <div className="flex flex-col">
            <div className={"w-4/5"}>
              <InputField
                type={"text"}
                id={"privacyUrl"}
                label={t("T_PRIVACY_POLICY_LABEL")}
                placeholder={""}
                value={privacyUrl.value}
                onChange={(value: any) => {
                  setPrivacyUrl({ ...privacyUrl, error: "", value });
                }}
                disabled={false}
                error={privacyUrl.error}
                asterixFirst
                required={false}
              />
            </div>
          </div>
        </Card>

        <UploadLanguageCard
          title={t("T_DISCLAIMER_POLICY")}
          setData={setDisclaimerData}
          ref={disclaimerFileRef}
          data={disclaimerData}
          addButtonTitle={t("T_LANGUAGE_DISCLAIMER_ADD_BUTTON")}
          pickerTitle={t("T_LANGUAGE")}
          fileKey={`${AppConstants.getDisclaimerGCPPath()}${AppConstants.ASSETS_LOCALISATION_DISCLAIMER_FILE_PREFIX}${disclaimerData.localeName}.${AppConstants.DATA_TYPE_JSON}`}
        ></UploadLanguageCard>

        <UploadLanguageCard
          title={t("T_ADVERSE_POLICY")}
          setData={setAdverseEventsData}
          ref={adverseEventsFileRef}
          data={adverseEventsData}
          addButtonTitle={t("T_LANGUAGE_ADVERSE_ADD_BUTTON")}
          pickerTitle={t("T_LANGUAGE")}
          fileKey={`${AppConstants.getAdverseGCPPath()}${AppConstants.ASSETS_LOCALISATION_ADVERSE_FILE_PREFIX}${adverseEventsData.localeName}.${AppConstants.DATA_TYPE_JSON}`}
        ></UploadLanguageCard>
      </div>
    </LoaderContainer>
  );
};

export default Policies;
